/**
 * CharacterFocusView — sidebar drill-in for "looking at a character".
 *
 * Renders the character's name, a placeholder slot for a future Status
 * panel (combat follow-up), and the list of currently-visible worn items
 * fetched via ``useVisibleWornItems``. Each worn-item row is a button
 * that delegates to ``onItemClick`` so the parent can push the next
 * focus-stack entry (drill into an item).
 *
 * The character description is not yet fetched here — Phase A's right
 * sidebar already shows the character name from the room state, and
 * pulling a fuller description string is tracked as a follow-up to this
 * task (it requires either a new endpoint or extending the look payload
 * to expose ``CharacterState.get_display_description``).
 */

import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { humanizeRegion, humanizeRegionLayer } from '../humanizeRegionLayer';
import { useVisibleMarkings } from '../hooks/useVisibleMarkings';
import { useVisibleWornItems } from '../hooks/useVisibleWornItems';
import { TargetMenu } from '@/game/target-menu/TargetMenu';

interface CharacterFocusViewProps {
  character: { id: number; name: string; personaId?: number | null };
  /**
   * The id of the requester's currently active character — passed to the
   * backend as the ``observer`` query parameter so visibility rules
   * (same room / self-look / staff) can be enforced. ``null`` when no
   * puppet is active; the worn-items list is then disabled.
   */
  observerId: number | null;
  onItemClick: (item: { id: number; name: string }) => void;
  accountId?: number | null;
  className?: string;
}

export function CharacterFocusView({
  character,
  observerId,
  onItemClick,
  accountId = null,
  className,
}: CharacterFocusViewProps) {
  const { data: visibleItems = [], isLoading } = useVisibleWornItems(
    character.id,
    observerId ?? undefined
  );
  const { data: visibleMarkings = [] } = useVisibleMarkings(character.id, observerId ?? undefined);

  const renderVisibleItems = () => {
    if (isLoading) {
      return (
        <div className="space-y-2" data-testid="visible-worn-loading">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      );
    }
    if (visibleItems.length === 0) {
      return <p className="text-sm italic text-muted-foreground">Nothing visible.</p>;
    }
    return (
      <ul className="space-y-1">
        {visibleItems.map((item) => {
          const ownerPersonaId = item.owner_persona_id ?? character.personaId;
          const row = (
            <button
              type="button"
              onClick={() => onItemClick({ id: item.id, name: item.display_name })}
              className="flex min-h-11 w-full items-baseline justify-between gap-2 rounded-md p-2 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="truncate text-sm">{item.display_name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {humanizeRegionLayer(item.body_region, item.equipment_layer)}
              </span>
            </button>
          );
          return (
            <li key={item.id}>
              {accountId !== null && ownerPersonaId != null ? (
                <TargetMenu
                  partition={`account-${accountId}`}
                  actorId={observerId}
                  target={{
                    kind: 'items',
                    target_id: item.id,
                    owner_persona_id: ownerPersonaId,
                  }}
                >
                  {row}
                </TargetMenu>
              ) : (
                row
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={cn('flex flex-col gap-4 p-4', className)}>
      <header>
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-bold">{character.name}</h2>
          <Link
            to={`/characters/${character.id}`}
            target="_blank"
            rel="noopener"
            className="min-h-11 rounded px-2 py-2 text-xs font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Open ${character.name} character sheet`}
          >
            Character sheet
          </Link>
        </div>
      </header>

      {/*
        Status placeholder — populated by the combat roadmap (separate phase).
        Rendered as an empty marker div so the sidebar layout is stable when
        the slot lights up. Hidden + aria-hidden until then.
      */}
      <div data-placeholder="status" className="hidden" aria-hidden="true" />

      <section aria-labelledby="worn-heading">
        <h3
          id="worn-heading"
          className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Wearing
        </h3>

        {renderVisibleItems()}
      </section>

      {visibleMarkings.length > 0 && (
        <section aria-labelledby="markings-heading">
          <h3
            id="markings-heading"
            className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Markings
          </h3>
          <ul className="space-y-1">
            {visibleMarkings.map((marking) => (
              <li
                key={marking.id}
                className="flex items-baseline justify-between gap-2 rounded-md p-2"
                title={marking.description || undefined}
              >
                <span className="truncate text-sm">{marking.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {humanizeRegion(marking.body_region)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
